import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import HubertTabs from 'components/HubertUI/HubertTabs/index.tsx';
import { ScrollArea } from 'components/ui/scroll-area';
import { useUser } from 'hooks/useUser';
import { useAppSelector } from 'rtk/query/hooks.ts';
import { selectQuestionMode } from 'rtk/questionEditorSelector.ts';
import { UserGroup } from 'types/accessControll.types.ts';
import { isDeveloper, isHubertAdminUser, isRecruiterUser } from 'types/user.types.ts';
import { ECreateQuestionMode, useQuestionEditorContext } from '../context/create-question.context.tsx';
import CreateTextBubble from '../CreateTextBubble/index.tsx';
import PostQuestionActions from '../PostQuestionActions/index.tsx';
import PreQuestionActions from '../PreQuestionActions/index.tsx';
import QuestionInTemplatesList from '../QuestionInTemplatesList/index.tsx';
import QuestionPreviewContainer from '../QuestionPreviewContainer/index.tsx';
import QuestionRules from '../rules/QuestionRules/index.tsx';
import { ShowIfUserIsInUserGroup } from '../ShowIfUserIsInUserGroup.tsx';
import TemplateInterviewSummaryBlock from '../TemplateInterviewSummaryBlock/index.tsx';
import TemplateInterviewSummaryLabel from '../TemplateInterviewSummaryLabel/index.tsx';
import ClassificationInfo from './ClassificationInfo.tsx';
import DeferrableSwitch from './DeferrableSwitch.tsx';
import AliasInput from './AliasInput.tsx';
import LanguageBadgesBar from './LanguageBadgesBar.tsx';
import { LanguageSelector, RecruiterScopeSelector, ScopeSelector, TypeSelector } from './Selectors.tsx';
import SendDataToLookerSwitch from './SendDataToLookerSwitch.tsx';
import TemplateEditInfoText from './TemplateEditInfoText.tsx';
import TypeSpecificEditor from './TypeSpecificEditor.tsx';

const EditorContent = () => {
  const { questionId } = useQuestionEditorContext();
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const inTemplate = [
    ECreateQuestionMode.InTemplateEdit,
    ECreateQuestionMode.InTemplateCreate,
    ECreateQuestionMode.InTemplateCopy,
  ].includes(mode);
  const showPreview = ECreateQuestionMode.Edit === mode && !inTemplate;
  const user = useUser();
  const recruiter = isRecruiterUser(user);
  const admin = isHubertAdminUser(user);

  const fields = (
    <div className="flex flex-col justify-between gap-10 pt-2">
      <LanguageBadgesBar />
      {admin && <SendDataToLookerSwitch />}
      <TemplateEditInfoText questionId={questionId} />
      <ScopeSelector />
      {recruiter && <RecruiterScopeSelector user={user} />}
      <TypeSelector />
      <LanguageSelector />
      <AliasInput />
      <ClassificationInfo />
      <CreateTextBubble />
      <TypeSpecificEditor />
      {/* Developers only until the whole complement feature ships. */}
      {isDeveloper(user) && <DeferrableSwitch />}
      <TemplateInterviewSummaryBlock />
      <TemplateInterviewSummaryLabel />
      <ShowIfUserIsInUserGroup group={UserGroup.HUBERT}>
        <PreQuestionActions />
        <PostQuestionActions />
      </ShowIfUserIsInUserGroup>
    </div>
  );

  if (inTemplate) {
    return <div className="w-full">{fields}</div>;
  }

  return (
    <div className={clsx('grid', showPreview ? 'grid-cols-3' : 'grid-cols-1', 'gap-4', 'h-full w-full')}>
      <ScrollArea className="col-span-2 flex max-h-[70vh] w-full flex-row gap-4 pr-2">{fields}</ScrollArea>

      {showPreview ? (
        <div className={clsx('col-span-1', 'h-full w-full', 'max-h-full', 'rounded-md border')}>
          <QuestionPreviewContainer className="h-full" />
        </div>
      ) : null}
    </div>
  );
};

const MainPanel = () => {
  const { t } = useTranslation('Questionsv2');
  const { questionId } = useQuestionEditorContext();
  const user = useUser();
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));

  // In-template EDIT keeps using the EditContainer side panel (EditQuestionRulesInTemplate) for
  // rules — no tabs here, so as not to offer two competing rules UIs for the same question.
  if (mode === ECreateQuestionMode.InTemplateEdit) {
    return <EditorContent />;
  }

  if (!user) {
    return <div>{t('mainPanel.userNotAvailable')}</div>;
  }

  return (
    <HubertTabs defaultValue="editor">
      <HubertTabs.List className="border-b-0">
        <HubertTabs.Trigger value="editor">{t('mainPanel.tabs.editor')}</HubertTabs.Trigger>
        <HubertTabs.Trigger value="rules">{t('mainPanel.tabs.rules')}</HubertTabs.Trigger>
        <HubertTabs.Trigger value="templates">{t('mainPanel.tabs.templates')}</HubertTabs.Trigger>
      </HubertTabs.List>
      <HubertTabs.Content value="editor">
        <EditorContent />
      </HubertTabs.Content>
      <HubertTabs.Content value="rules">
        <QuestionRules />
      </HubertTabs.Content>
      <HubertTabs.Content value="templates">
        <QuestionInTemplatesList />
      </HubertTabs.Content>
    </HubertTabs>
  );
};

export default MainPanel;
